import React, { useState, useEffect, useMemo, useRef } from 'react';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { getPriorityInfo } from '../../utils/messageUtils';
import { resolveDateTags } from '../../utils/dateCommandUtils';
import { FiClipboard, FiStar, FiChevronDown, FiChevronRight, FiCheckSquare, FiSquare, FiTag, FiMoreVertical, FiEdit2, FiTrash2, FiEyeOff, FiPlus, FiEye, FiCheck, FiX, FiMaximize2 } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useMessages } from '../../context/MessagesContext';
import { useNavigate } from 'react-router-dom';
import DarkSkeleton from '../shared/DarkSkeleton';
import DeleteCustomTabModal from '../modals/DeleteCustomTabModal';
import MessageSortModal from '../modals/MessageSortModal';
import { usePlanPermissions, getCurrentPlan, hasCustomTabsPermission } from '../../utils/planPermissions';
import { toast } from '../../utils/customToast';
import useCodeBlockEnhancer, { highlightHtml } from '../../hooks/useCodeBlockEnhancer';

const apiBase = process.env.REACT_APP_API_URL || '/api';

const MESSAGE_TYPE_COLORS = {
    'Rappel mensuel': '#3b82f6',
    'Rappel hebdomadaire': '#8b5cf6',
    'Rappel quotidien': '#06b6d4',
    'Notification': '#f59e0b',
    'Information': '#10b981',
    'Alerte': '#ef4444',
    'Mise à jour': '#6366f1'
};

const COLORS = [
    '#ef4444', // Red
    '#f97316', // Orange
    '#10b981', // Emerald
    '#06b6d4', // Cyan
    '#3b82f6', // Blue
    '#ec4899'  // Pink
];

const getContrastText = (hex = '#ffffff') => {
    const h = hex.replace('#', '');
    if (h.length !== 6) return '#000';
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#000' : '#fff';
};

const MessageCard = ({
    message,
    handleCopy,
    copied,
    isFavorite,
    handleToggleFavorite,
    collapsed,
    handleToggleCollapsed,
    renderContentWithMentions,
    setFullMessage,
    onContentClick,
    typeColors,
    selectionMode = false,
    isSelected = false,
    onSelect,
    draggable = false,
    isDragOver = false,
    isDragging = false,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,
    onDragEnd,
    showColoredBorder = true
}) => {
    const [isMobile, setIsMobile] = useState(false);
    const contentRef = useRef(null);

    // Boutons "Copier" sur les blocs de code dans la carte message
    useCodeBlockEnhancer(contentRef, [message?.content]);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 639px)');
        const set = () => setIsMobile(mq.matches);
        set();
        if (mq.addEventListener) mq.addEventListener('change', set);
        else if (mq.addListener) mq.addListener(set);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', set);
            else if (mq.removeListener) mq.removeListener(set);
        };
    }, []);

    const truncateLabel = (label) => {
        if (!label) return '';
        if (isMobile && label.length > 9) return label.slice(0, 9) + '…';
        return label;
    };

    const priorityInfo = getPriorityInfo(message.priority);
    const typeColor = (typeColors && typeColors[message.type]) || MESSAGE_TYPE_COLORS[message.type] || '#e5e7eb';
    const isCopied = copied[message._id];

    return (
        <div
            draggable={draggable}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
            className={`relative flex flex-col rounded-xl border transition-all duration-200 min-w-0 overflow-hidden ${
                isDragOver
                    ? 'border-dashed border-primary bg-primary/5 scale-[1.01] shadow-lg'
                    : selectionMode && isSelected
                        ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md'
            } ${isDragging ? 'opacity-40' : ''} ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
            style={{ borderWidth: 1, borderColor: selectionMode && isSelected ? '#3b82f6' : showColoredBorder ? typeColor : undefined }}
        >
            {/* ── Meta row ─────────────────────────────────────────── */}
            <div className="flex items-center gap-2 px-3 pt-2.5 pb-0">
                {/* Left: selection or drag */}
                {selectionMode ? (
                    <button
                        onClick={(e) => { e.stopPropagation(); onSelect?.(message._id); }}
                        className="flex-shrink-0 text-gray-400 hover:text-blue-500 transition"
                        aria-label={isSelected ? "Désélectionner" : "Sélectionner"}
                        type="button"
                    >
                        {isSelected
                            ? <FiCheckSquare className="h-4 w-4 text-blue-500" aria-hidden="true" />
                            : <FiSquare className="h-4 w-4" aria-hidden="true" />}
                    </button>
                ) : draggable ? (
                    <span className="flex-shrink-0 text-gray-300 dark:text-gray-600 cursor-grab active:cursor-grabbing select-none" title="Déplacer">
                        <FiMaximize2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                ) : null}

                {/* Type badge */}
                <span
                    className="text-xs px-1.5 py-0.5 rounded font-semibold flex-shrink-0"
                    style={{
                        backgroundColor: typeColor,
                        color: getContrastText(typeColor),
                    }}
                >
                    {truncateLabel(message.type)}
                </span>

                {/* Role */}
                {message.role && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 truncate min-w-0">
                        {truncateLabel(message.role)}
                    </span>
                )}

                {/* Spacer */}
                <div className="flex-1" />

                {/* Star */}
                <button
                    onClick={(e) => handleToggleFavorite(message._id, e)}
                    className={`flex-shrink-0 p-1 rounded transition ${
                        isFavorite(message._id)
                            ? 'text-yellow-400 hover:text-yellow-500'
                            : 'text-gray-300 dark:text-gray-600 hover:text-yellow-400'
                    }`}
                    aria-label={isFavorite(message._id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                    type="button"
                >
                    <FiStar className={`h-3.5 w-3.5 ${isFavorite(message._id) ? 'fill-current' : ''}`} aria-hidden="true" />
                </button>
            </div>

            {/* ── Title row ─────────────────────────────────────────── */}
            <div className="flex items-start gap-1.5 px-3 pt-2 pb-1">
                <button
                    onClick={(e) => { e.stopPropagation(); handleToggleCollapsed(message._id); }}
                    className="flex-shrink-0 mt-0.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition rounded p-0.5"
                    aria-label={collapsed ? "Développer" : "Réduire"}
                    type="button"
                >
                    {collapsed ? <FiChevronRight className="h-4 w-4" aria-hidden="true" /> : <FiChevronDown className="h-4 w-4" aria-hidden="true" />}
                </button>
                <h2
                    className="text-sm font-semibold text-gray-900 dark:text-white leading-snug cursor-pointer hover:text-primary dark:hover:text-primary transition flex-1 min-w-0"
                    onClick={() => setFullMessage(message)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFullMessage(message); } }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Ouvrir le message: ${message.title}`}
                >
                    {message.title}
                </h2>
            </div>

            {/* ── Content ───────────────────────────────────────────── */}
            {!collapsed && (
                <div className="relative px-3 pb-1">
                    <div
                        ref={contentRef}
                        className="text-gray-600 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none max-h-28 overflow-hidden min-w-0 text-xs leading-relaxed"
                        style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                        dangerouslySetInnerHTML={{ __html: highlightHtml(sanitizeHtml(renderContentWithMentions(message))) }}
                        onClick={onContentClick}
                    />
                    {message.content && message.content.length > 200 && (
                        <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pointer-events-none" />
                    )}
                </div>
            )}

            {/* ── Footer ────────────────────────────────────────────── */}
            <div className="mt-auto flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-100 dark:border-gray-700/60">
                {/* Priority */}
                <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border ${priorityInfo.bgColor} ${priorityInfo.textColor} ${priorityInfo.borderColor}`}
                    title={priorityInfo.title}
                >
                    <span className="text-xs leading-none">{priorityInfo.icon}</span>
                    <span>{priorityInfo.label}</span>
                </span>

                {/* Copy CTA */}
                <button
                    onClick={(e) => { e.stopPropagation(); handleCopy(message._id, message.content); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        isCopied
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-primary/10 hover:bg-primary/20 text-primary'
                    }`}
                    aria-label="Copier le texte"
                    type="button"
                >
                    {isCopied
                        ? <><FiCheck className="h-3.5 w-3.5" aria-hidden="true" /> Copié</>
                        : <><FiClipboard className="h-3.5 w-3.5" aria-hidden="true" /> Copier</>
                    }
                </button>
            </div>
        </div>
    );
};

const MessageList = ({
    messages = [],
    onEdit,
    onDelete,
    columns = 3,
    loading = false,
    openMessageId = null,
    setFullMessage,
    selectionMode = false,
    selectedMessages = new Set(),
    onMessageSelect,
    typeColors: propTypeColors,
    onReorder,
    customTabsApi = {}
}) => {
    const [typeColors, setTypeColors] = useState({ ...MESSAGE_TYPE_COLORS, ...(propTypeColors || {}) });
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 9;
    const [copied, setCopied] = useState({});
    const [activeTab, setActiveTab] = useState('all');
    const [activeMenuId, setActiveMenuId] = useState(null);
    const [deleteTabModal, setDeleteTabModal] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newTabName, setNewTabName] = useState('');
    const [selectedColor, setSelectedColor] = useState('#6366f1');
    const [editingTabId, setEditingTabId] = useState(null);
    const [editTabName, setEditTabName] = useState('');
    const [showHiddenTabs, setShowHiddenTabs] = useState(false);
    const { user, toggleFavorite, toggleCollapsed: toggleCollapsedAPI, getAuthHeaders, activeTenantId } = useAuth();
    const { isCollapsed, toggleCollapsed } = useMessages();
    const navigate = useNavigate();
    const hiddenTabsRef = useRef(null);
    const [forceLoading, setForceLoading] = useState(true);
    const [showSortModal, setShowSortModal] = useState(false);

    // Drag-and-drop state
    const [draggedId, setDraggedId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);

    const handleDragStart = (e, messageId) => {
        setDraggedId(messageId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', messageId);
    };

    const handleDragOver = (e, messageId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (messageId !== draggedId) setDragOverId(messageId);
    };

    const handleDragLeave = () => setDragOverId(null);

    const handleDrop = (e, targetId) => {
        e.preventDefault();
        setDragOverId(null);
        if (!draggedId || draggedId === targetId) {
            setDraggedId(null);
            return;
        }
        const arr = [...messages];
        const fromIdx = arr.findIndex(m => m._id === draggedId);
        const toIdx = arr.findIndex(m => m._id === targetId);
        if (fromIdx === -1 || toIdx === -1) { setDraggedId(null); return; }
        const [removed] = arr.splice(fromIdx, 1);
        arr.splice(toIdx, 0, removed);
        setDraggedId(null);
        onReorder?.(arr.map(m => m._id));
    };

    const handleDragEnd = () => {
        setDraggedId(null);
        setDragOverId(null);
    };
    const [isMobile, setIsMobile] = useState(false);
    
    // États pour les mentions
    const [allMessages, setAllMessages] = useState([]);
    const [allTodos, setAllTodos] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [allUsers, setAllUsers] = useState([]);
    const currentUserId = user?._id || user?.id || null;

    // Custom tabs — données fournies par Messages.jsx via customTabsApi pour éviter un double fetch
    const {
        customTabs = [],
        canUseCustomTabs = false,
        createCustomTab,
        updateCustomTab,
        deleteCustomTab,
        addMessagesToTab,
        removeMessagesFromTab,
        hideTabForUser,
        showTabForUser,
        fetchCustomTabs
    } = customTabsApi;
    
    const currentPlan = getCurrentPlan(user, activeTenantId);
    const { canUseCustomTabs: planAllowsCustomTabs } = usePlanPermissions(currentPlan);
    
    // Fonction centralisée pour vérifier les permissions de custom tabs
    const hasTabsPermission = canUseCustomTabs || planAllowsCustomTabs || hasCustomTabsPermission(currentPlan);

    // Réinitialiser la page quand l'onglet actif ou la liste de messages change
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, messages]);

    useEffect(() => {
        const timer = setTimeout(() => setForceLoading(false), 500);
        return () => clearTimeout(timer);
    }, []);

    // Détection mobile
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 639px)');
        const set = () => setIsMobile(mq.matches);
        set();
        if (mq.addEventListener) mq.addEventListener('change', set);
        else if (mq.addListener) mq.addListener(set);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', set);
            else if (mq.removeListener) mq.removeListener(set);
        };
    }, []);

    // Recharger les onglets personnalisés quand l'utilisateur ou le tenant changent
    useEffect(() => {
        if (user && activeTenantId && hasTabsPermission) {
            fetchCustomTabs();
        }
        
        // Écouter les mises à jour du profil utilisateur
        const handleProfileUpdate = () => {
            if (user && activeTenantId && hasTabsPermission) {
                fetchCustomTabs();
            }
        };
        
        window.addEventListener('userProfileUpdated', handleProfileUpdate);
        
        return () => {
            window.removeEventListener('userProfileUpdated', handleProfileUpdate);
        };
    }, [user, activeTenantId, hasTabsPermission, fetchCustomTabs]);

    useEffect(() => {
        setAllMessages(messages);
    }, [messages]);

    // Gérer les clics à l'extérieur du dropdown des onglets masqués
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (hiddenTabsRef.current && !hiddenTabsRef.current.contains(event.target)) {
                setShowHiddenTabs(false);
            }
        };

        if (showHiddenTabs) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showHiddenTabs]);

    // Fonction pour gérer la suppression d'onglet
    const handleDeleteTab = (tab) => {
        setDeleteTabModal(tab);
        setActiveMenuId(null);
    };

    const handleShowTab = async (tab) => {
        try {
            await showTabForUser(tab._id);
            setShowHiddenTabs(false);
        } catch (error) {
            console.error('Erreur lors de la réactivation de l\'onglet:', error);
        }
    };

    const confirmDeleteTab = async () => {
        if (deleteTabModal) {
            const tabName = deleteTabModal.name;
            const success = await deleteCustomTab(deleteTabModal._id);
            if (success) {
                toast.success(`Onglet "${tabName}" supprimé avec succès`, {
                    autoClose: 5000
                });
            }
            // Si l'onglet supprimé est actif, revenir à 'all'
            if (activeTab === `custom-${deleteTabModal._id}`) {
                setActiveTab('all');
            }
            setDeleteTabModal(null);
        }
    };

    // Fonction pour basculer la visibilité d'un onglet
    const handleToggleVisibility = async (tab) => {
        try {
            if (tab.isVisible) {
                await hideTabForUser(tab._id);
                // Si l'onglet masqué est actif, revenir à 'all'
                if (activeTab === `custom-${tab._id}`) {
                    setActiveTab('all');
                }
            } else {
                await showTabForUser(tab._id);
            }
            setActiveMenuId(null);
        } catch (error) {
            console.error('Erreur lors du basculement de visibilité:', error);
        }
    };

    // Fonction pour créer un nouvel onglet
    const handleCreateTab = async () => {
        const tabName = String(newTabName || '').trim();
        if (!tabName) {
            toast.error('Le nom de l\'onglet ne peut pas être vide');
            return;
        }

        try {
            await createCustomTab({
                name: tabName,
                color: selectedColor,
                isVisible: true
            });
            
            // Réinitialiser le formulaire
            setNewTabName('');
            setSelectedColor(COLORS[0]);
            setShowCreateForm(false);
            
            toast.success('Onglet créé avec succès');
        } catch (error) {
            console.error('Erreur lors de la création de l\'onglet:', error);
            const errorMessage = error.message || 'Erreur lors de la création de l\'onglet';
            toast.error(errorMessage);
        }
    };

    // Fonction pour annuler la création
    const handleCancelCreate = () => {
        setNewTabName('');
        setSelectedColor(COLORS[0]);
        setShowCreateForm(false);
    };

    // Fonction pour commencer l'édition d'un onglet
    const handleStartEdit = (tab) => {
        setEditingTabId(tab._id);
        setEditTabName(tab.name);
        setActiveMenuId(null);
    };

    // Fonction pour sauvegarder le nouveau nom
    const handleSaveEdit = async () => {
        const tabName = String(editTabName || '').trim();
        if (!tabName) {
            toast.error('Le nom de l\'onglet ne peut pas être vide');
            return;
        }

        try {
            await updateCustomTab(editingTabId, { name: tabName });
            setEditingTabId(null);
            setEditTabName('');
            toast.success('Onglet renommé avec succès');
        } catch (error) {
            console.error('Erreur lors du renommage:', error);
            const errorMessage = error.message || 'Erreur lors du renommage de l\'onglet';
            toast.error(errorMessage);
        }
    };

    // Fonction pour annuler l'édition
    const handleCancelEdit = () => {
        setEditingTabId(null);
        setEditTabName('');
    };

    // Référence pour le menu
    const menuRef = React.useRef(null);

    // Fermer le menu lors d'un clic extérieur
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setActiveMenuId(null);
            }
        };

        if (activeMenuId) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [activeMenuId]);

    // Charger les messages et todos pour les mentions
    useEffect(() => {
        (async () => {
            try {
                const [mRes, tRes] = await Promise.all([
                    fetch(`${apiBase}/messages`, { headers: getAuthHeaders() }),
                    fetch(`${apiBase}/todos`, { headers: getAuthHeaders() })
                ]);
                if (mRes.ok) setAllMessages(await mRes.json());
                if (tRes.ok) setAllTodos(await tRes.json());
            } catch (e) {
                console.error('Error loading data for mentions:', e);
            }
        })();
    }, [getAuthHeaders]);

    // Synchroniser typeColors avec la prop si elle est fournie
    useEffect(() => {
        if (propTypeColors && Object.keys(propTypeColors).length > 0) {
            setTypeColors({ ...MESSAGE_TYPE_COLORS, ...propTypeColors });
        }
    }, [propTypeColors]);

    // Load tenant message type colors and watch for changes (fallback si pas de prop)
    useEffect(() => {
        // Si on a déjà les couleurs via la prop, pas besoin de charger depuis l'API
        if (propTypeColors && Object.keys(propTypeColors).length > 0) return;

        let mounted = true;
        const loadTypeColors = async () => {
            if (!activeTenantId) return;
            try {
                const res = await fetch(`${apiBase}/tenants/${activeTenantId}/settings/messages`, { headers: getAuthHeaders() });
                if (!res.ok) return;
                const data = await res.json();
                const types = Array.isArray(data.messageTypes) ? data.messageTypes : [];
                const mapped = { ...MESSAGE_TYPE_COLORS };
                types.forEach(t => {
                    if (!t) return;
                    if (typeof t === 'string') return; // no color available
                    if (t.name) mapped[t.name] = t.color || mapped[t.name] || '#3b82f6';
                });
                if (mounted) setTypeColors(mapped);
            } catch (err) {
                // ignore silently
            }
        };

        loadTypeColors();

        const handler = () => loadTypeColors();
        window.addEventListener('messageSettingsChanged', handler);
        return () => { mounted = false; window.removeEventListener('messageSettingsChanged', handler); };
    }, [activeTenantId, getAuthHeaders, propTypeColors]);

    // Si un id est passé via la query string (navigué depuis Header), ouvrir le message
     useEffect(() => {
        if (openMessageId && messages.length > 0) {
            const msg = messages.find(m => m._id === openMessageId);
            if (msg) {
                setFullMessage(msg);
                // Nettoyer l'URL après ouverture
                navigate('/messages', { replace: true });
            }
        }
    }, [openMessageId, messages, navigate, setFullMessage]);

    // Charger les utilisateurs pour les mentions
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${apiBase}/tenants/${activeTenantId}/users`, {
                    headers: getAuthHeaders()
                });
                if (res.ok) setAllUsers(await res.json());
            } catch (e) {
                console.error('Error fetching users:', e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getAuthHeaders, activeTenantId]);

    const favoriteMessages = useMemo(() => 
        messages.filter(m => user?.favoriteMessages?.includes(m._id)),
        [messages, user?.favoriteMessages]
    );

    const nonFavoriteMessages = useMemo(() => 
        messages.filter(m => !user?.favoriteMessages?.includes(m._id)),
        [messages, user?.favoriteMessages]
    );

    const viewMode = user?.favoritesViewMode || 'inline';

    const { visibleFavorites, visibleMessages } = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        const end = start + PAGE_SIZE;
        if (viewMode === 'inline') {
            // Inline : favoris + non-favoris paginés ensemble
            const all = [...favoriteMessages, ...nonFavoriteMessages];
            const page = all.slice(start, end);
            const favEnd = Math.min(favoriteMessages.length, end);
            const favStart = Math.min(favoriteMessages.length, start);
            return {
                visibleFavorites: favoriteMessages.slice(favStart, favEnd),
                visibleMessages: page.filter(m => !favoriteMessages.some(f => f._id === m._id))
            };
        }
        return {
            visibleFavorites: favoriteMessages.slice(start, end),
            visibleMessages: nonFavoriteMessages.slice(start, end)
        };
    }, [favoriteMessages, nonFavoriteMessages, currentPage, viewMode]);

    const isFavorite = (messageId) => {
        return user?.favoriteMessages?.includes(messageId);
    };

    const handleToggleFavorite = async (messageId, e) => {
        e.stopPropagation();
        await toggleFavorite(messageId);
    };

    const handleToggleCollapsed = (messageId) => {
        toggleCollapsed(messageId);
        toggleCollapsedAPI(messageId);
    };

    const handleCopy = async (id, htmlContent) => {
        try {
            // Créer un élément temporaire pour gérer le HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = sanitizeHtml(htmlContent);
            
            // Copier à la fois le texte brut et le HTML formaté
            const textContent = tempDiv.textContent || tempDiv.innerText || '';
            
            // Utiliser l'API Clipboard moderne pour copier avec formatage
            if (navigator.clipboard && window.ClipboardItem) {
                const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
                const textBlob = new Blob([textContent], { type: 'text/plain' });
                
                const clipboardItem = new ClipboardItem({
                    'text/html': htmlBlob,
                    'text/plain': textBlob
                });
                
                await navigator.clipboard.write([clipboardItem]);
            } else {
                // Fallback pour les navigateurs plus anciens
                await navigator.clipboard.writeText(textContent);
            }
            
            setCopied(prev => ({ ...prev, [id]: true }));
            setTimeout(() => setCopied(prev => ({ ...prev, [id]: false })), 2000);
        } catch (err) {
            console.error('Erreur lors de la copie:', err);
        }
    };

    // const handleSort = (messagesToSort) => {
    //     if (!canUseCustomTabs) return;
    //     setShowSortModal(true);
    // };

    const renderContentWithMentions = (msg) => {
        let content = msg.content || '';

        // Résolution dynamique des balises de dates {{date:Commande}}
        content = resolveDateTags(content);

        // Remplacer les mentions @user:id[Name]
        content = content.replace(/@user:([A-Za-z0-9_-]+)\[([^\]]+)\]/g, (m, id, name) => {
            const isMentioned = id === currentUserId;
            const mentionClass = isMentioned ? 'user-mention mentioned-user' : 'user-mention';
            return `<span class="${mentionClass}" data-user-id="${id}">${name}</span>`;
        });
        
        // Remplacer les mentions @todo:
        content = content.replace(/@todo:([A-Za-z0-9_-]+)/g, (m, id) => {
            const todo = allTodos?.find(t => t._id === id);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = sanitizeHtml(todo?.text || '');
            const label = tempDiv.textContent || tempDiv.innerText || `todo:${id}`;
            const truncated = label.length > 40 ? label.slice(0, 40) + '...' : label;
            return `<a href="app://todo/${id}" class="mention-link text-accent underline hover:opacity-80 font-semibold" data-mention-type="todo" data-mention-id="${id}">${truncated}</a>`;
        });
        
        // Remplacer les mentions @msg:
        content = content.replace(/@msg:([A-Za-z0-9_-]+)/g, (m, id) => {
            const targetMsg = allMessages?.find(m => m._id === id);
            const label = targetMsg?.title || `msg:${id}`;
            const truncated = label.length > 40 ? label.slice(0, 40) + '...' : label;
            return `<a href="app://msg/${id}" class="mention-link text-accent underline hover:opacity-80 font-semibold" data-mention-type="msg" data-mention-id="${id}">${truncated}</a>`;
        });
        
        return content;
    };

    const gridContainerClass = columns === 1
        ? 'grid grid-cols-1 gap-4'
        : columns === 2
            ? 'grid grid-cols-1 sm:grid-cols-2 gap-4'
            : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4';

    const isLoading = loading || forceLoading;
    
    if (isLoading) {
        return (
            <div className={gridContainerClass}>
                {[...Array(3)].map((_, i) => (
                    <div
                        key={i}
                        className="p-5 border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl shadow-sm flex flex-col h-[282.15px]"
                        aria-hidden="true"
                    >
                        <div className="flex items-center mb-2">
                            <div className="mr-2"><DarkSkeleton width={64} height={12} /></div>
                            <div className="mr-2"><DarkSkeleton width={96} height={16} /></div>
                            <div className="ml-auto flex space-x-2">
                                <DarkSkeleton width={32} height={32} />
                                <DarkSkeleton width={32} height={32} />
                            </div>
                        </div>
                        <div className="mb-3"><DarkSkeleton width="85%" height={20} /></div>
                        <div className="space-y-2">
                            <DarkSkeleton height={10} />
                            <DarkSkeleton height={10} width="90%" />
                            <DarkSkeleton height={10} width="60%" />
                            <DarkSkeleton height={10} />
                            <DarkSkeleton height={10} width="80%" />
                        </div>
                        <div className="mt-3 self-end"><DarkSkeleton width={80} height={12} /></div>
                    </div>
                ))}
            </div>
        );
    }

    const handleContentClick = (e) => {
        const target = e.target;
        if (target.classList.contains('mention-link')) {
            e.preventDefault();
            e.stopPropagation();
            
            const mentionType = target.getAttribute('data-mention-type');
            const mentionId = target.getAttribute('data-mention-id');
            
            if (mentionType === 'todo') {
                // Dispatcher l'événement pour ouvrir le TodoPanel
                window.dispatchEvent(new CustomEvent('openTodo', { detail: { id: mentionId } }));
            } else if (mentionType === 'msg') {
                // Trouver le message et l'ouvrir en modal
                const targetMessage = messages.find(m => m._id === mentionId);
                if (targetMessage) {
                    setFullMessage(targetMessage);
                }
            }
        }
    };

    if (messages.length === 0) {
        return (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <p className="text-lg">Aucun message disponible.</p>
                <p className="text-sm mt-2">Créez votre premier message pour commencer.</p>
            </div>
        );
    }

    return (
        <>
            {viewMode === 'tabs' ? (
                <div>
                    <div className={`flex border-b dark:border-gray-700 mb-4 ${
                        isMobile ? 'overflow-x-auto scrollbar-hide' : 'flex-wrap'
                    }`}>
                        <button
                            onClick={() => setActiveTab('all')}
                            className={`px-6 py-3 font-semibold transition-colors flex-shrink-0 ${
                                activeTab === 'all'
                                    ? 'text-primary border-b-2 border-primary'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-primary'
                            }`}
                        >
                            Tous ({messages.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('favorites')}
                            className={`px-6 py-3 font-semibold transition-colors flex items-center gap-2 flex-shrink-0 ${
                                activeTab === 'favorites'
                                    ? 'text-primary border-b-2 border-primary'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-primary'
                            }`}
                        >
                            <FiStar className={favoriteMessages.length > 0 ? 'fill-current' : ''} aria-hidden="true" />
                            Favoris ({favoriteMessages.length})
                        </button>

                        {/* Onglets personnalisés avec boutons d'action intégrés */}
                        {hasTabsPermission && customTabs.filter(tab => tab.isVisible).map((tab) => {
                            const tabMessages = messages.filter(msg => tab.messageIds?.includes(msg._id));
                            const isActive = activeTab === `custom-${tab._id}`;
                            const isEditing = editingTabId === tab._id;
                            
                            return (
                                <div key={tab._id} className="relative group flex items-center flex-shrink-0">
                                    {isEditing ? (
                                        <div className="flex items-center gap-2 px-[0.375rem] py-1 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-700">
                                            <div 
                                                className="w-3 h-3 rounded-full" 
                                                style={{ backgroundColor: tab.color }}
                                            />
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="text"
                                                    value={editTabName}
                                                    onChange={(e) => setEditTabName(e.target.value)}
                                                    maxLength={50}
                                                    className={`px-2 py-1 text-sm border rounded bg-white dark:bg-gray-700 dark:border-gray-600 min-w-20 text-black dark:text-white ${
                                                        editTabName.length > 50 ? 'border-red-500' : ''
                                                    }`}
                                                    autoFocus
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleSaveEdit();
                                                        if (e.key === 'Escape') handleCancelEdit();
                                                    }}
                                                />
                                                <div className={`text-xs whitespace-nowrap ${
                                                    editTabName.length > 45 ? (editTabName.length > 50 ? 'text-red-500' : 'text-orange-500') : 'text-gray-500'
                                                }`}>
                                                    {editTabName.length}/50
                                                </div>
                                            </div>
                                            <span className="text-sm text-gray-500">({tabMessages.length})</span>
                                            <button
                                                onClick={handleSaveEdit}
                                                className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition"
                                                title="Sauvegarder"
                                                aria-label="Sauvegarder"
                                            >
                                                <FiCheck className="h-5 w-3 text-white dark:text-white flex-shrink-0" aria-hidden="true" />
                                            </button>
                                            <button
                                                onClick={handleCancelEdit}
                                                className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition"
                                                title="Annuler"
                                                aria-label="Annuler"
                                            >
                                                <FiX className="h-5 w-3 text-white dark:text-white flex-shrink-0" aria-hidden="true" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="relative flex items-center" ref={activeMenuId === tab._id ? menuRef : null}>
                                            <button
                                                onClick={() => setActiveTab(`custom-${tab._id}`)}
                                                className={`px-4 py-3 font-semibold transition-colors flex items-center gap-2 ${
                                                    isActive
                                                        ? 'text-primary border-b-2 border-primary'
                                                        : 'text-gray-600 dark:text-gray-400 hover:text-primary'
                                                }`}
                                            >
                                                <div 
                                                    className="w-3 h-3 rounded-full" 
                                                    style={{ backgroundColor: tab.color }}
                                                />
                                                <span className="truncate-tab-name" title={tab.name}>{tab.name}</span> ({tabMessages.length})
                                            </button>
                                            
                                            {/* Bouton menu "..." séparé - masqué pendant l'édition et en mobile */}
                                            {!isMobile && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setActiveMenuId(activeMenuId === tab._id ? null : tab._id);
                                                    }}
                                                    className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                                                    aria-label="Options de l'onglet"
                                                >
                                                    <FiMoreVertical className="w-3 h-3" aria-hidden="true" />
                                                </button>
                                            )}
                                            
                                            {activeMenuId === tab._id && (
                                                <div className="absolute top-10 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-50 min-w-32">
                                                    <button
                                                        onClick={() => handleStartEdit(tab)}
                                                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    >
                                                        <FiEdit2 className="w-3 h-3" aria-hidden="true" />
                                                        Renommer
                                                    </button>
                                                    <button
                                                        onClick={() => handleToggleVisibility(tab)}
                                                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    >
                                                        <FiEyeOff className="w-3 h-3" aria-hidden="true" />
                                                        Masquer
                                                    </button>
                                                    <div className="border-b dark:border-gray-700 px-3 hidden sm:block" />
                                                    <button
                                                        onClick={() => handleDeleteTab(tab)}
                                                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    >
                                                        <FiTrash2 className="w-3 h-3" />
                                                        Supprimer
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Formulaire de création d'onglet intégré - masqué sur mobile */}
                        {hasTabsPermission && (
                            <div className="hidden sm:flex items-center gap-2 ml-4">
                                {showCreateForm ? (
                                    <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 p-1 rounded-md border border-gray-700">
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={newTabName}
                                                onChange={(e) => setNewTabName(e.target.value)}
                                                maxLength={50}
                                                placeholder="Nom de l'onglet"
                                                className={`px-2 py-1 text-sm border rounded bg-white dark:bg-gray-700 dark:border-gray-600 w-32 text-black dark:text-white ${
                                                    newTabName.length > 50 ? 'border-red-500' : ''
                                                }`}
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleCreateTab();
                                                    if (e.key === 'Escape') handleCancelCreate();
                                                }}
                                            />
                                            <div className={`text-xs whitespace-nowrap ${
                                                newTabName.length > 45 ? (newTabName.length > 50 ? 'text-red-500' : 'text-orange-500') : 'text-gray-500'
                                            }`}>
                                                {newTabName.length}/50
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            {COLORS.map((color) => (
                                                <button
                                                    key={color}
                                                    onClick={() => setSelectedColor(color)}
                                                    className={`w-5 h-5 rounded-full border-2 transition ${
                                                        selectedColor === color
                                                            ? 'border-gray-900 dark:border-white scale-110'
                                                            : 'border-gray-300 dark:border-gray-600'
                                                    }`}
                                                    style={{ backgroundColor: color }}
                                                    title={color}
                                                />
                                            ))}
                                        </div>
                                        <button
                                            onClick={handleCreateTab}
                                            className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition"
                                            aria-label="Créer l'onglet"
                                        >
                                            <FiCheck className="h-5 w-3 text-white dark:text-white flex-shrink-0" aria-hidden="true" />
                                        </button>
                                        <button
                                            onClick={handleCancelCreate}
                                            className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition"
                                            aria-label="Annuler"
                                        >
                                            <FiX className="h-5 w-3 text-white dark:text-white flex-shrink-0" aria-hidden="true" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setShowCreateForm(true)}
                                            className="px-2 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition flex items-center gap-1 text-gray-600 dark:text-gray-400"
                                            title="Créer un nouvel onglet"
                                            aria-label="Créer un nouvel onglet"
                                        >
                                            <FiPlus className="w-4 h-4" aria-hidden="true" />
                                        </button>
                                        {customTabs.some(tab => !tab.isVisible) && (
                                            <div className="relative" ref={hiddenTabsRef}>
                                                <button
                                                    onClick={() => setShowHiddenTabs(!showHiddenTabs)}
                                                    className="px-2 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition flex items-center gap-1 text-gray-600 dark:text-gray-400"
                                                    title="Gérer les onglets masqués"
                                                    aria-label="Gérer les onglets masqués"
                                                >
                                                    <FiEyeOff className="w-4 h-4" aria-hidden="true" />
                                                </button>
                                                {showHiddenTabs && (
                                                    <div className="absolute top-10 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-2 z-50 min-w-48">
                                                        <div className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 mb-1">
                                                            Onglets masqués
                                                        </div>
                                                        {customTabs.filter(tab => !tab.isVisible).map((tab) => (
                                                            <div key={tab._id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700">
                                                                <div className="flex items-center gap-2">
                                                                    <div 
                                                                        className="w-3 h-3 rounded-full" 
                                                                        style={{ backgroundColor: tab.color }}
                                                                    />
                                                                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[120px]" title={tab.name}>{tab.name}</span>
                                                                </div>
                                                                <button
                                                                    onClick={() => handleShowTab(tab)}
                                                                    className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition"
                                                                    title="Réactiver cet onglet"
                                                                    aria-label="Réactiver cet onglet"
                                                                >
                                                                    <FiEye className="w-3 h-3" aria-hidden="true" />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {activeTab === 'all' && (
                        <div className={gridContainerClass}>
                            {/* Afficher TOUS les messages (favoris + non-favoris) */}
                            {messages.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((message) => (
                                <MessageCard
                                    key={message._id}
                                    message={message}
                                    handleCopy={handleCopy}
                                    copied={copied}
                                    isFavorite={isFavorite}
                                    handleToggleFavorite={handleToggleFavorite}
                                    collapsed={isCollapsed(message._id)}
                                    handleToggleCollapsed={handleToggleCollapsed}
                                    renderContentWithMentions={renderContentWithMentions}
                                    setFullMessage={setFullMessage}
                                    onContentClick={handleContentClick}
                                    typeColors={typeColors}
                                    selectionMode={selectionMode}
                                    isSelected={selectedMessages.has(message._id)}
                                    onSelect={onMessageSelect}
                                    draggable={!!onReorder && !selectionMode}
                                    isDragOver={dragOverId === message._id}
                                    isDragging={draggedId === message._id}
                                    onDragStart={(e) => handleDragStart(e, message._id)}
                                    onDragOver={(e) => handleDragOver(e, message._id)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, message._id)}
                                    onDragEnd={handleDragEnd}
                                    showColoredBorder={user?.showColoredBorder ?? true}
                                />
                            ))}
                        </div>
                    )}

                    {activeTab === 'favorites' && (
                        favoriteMessages.length === 0 ? (
                            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                                <FiStar className="h-16 w-16 mx-auto mb-4 opacity-50" aria-hidden="true" />
                                <p className="text-lg">Aucun favori</p>
                                <p className="text-sm mt-2">Cliquez sur l'étoile pour ajouter des messages à vos favoris</p>
                            </div>
                        ) : (
                            <div className={gridContainerClass}>
                                {visibleFavorites.map((message) => (
                                    <MessageCard
                                        key={message._id}
                                        message={message}
                                        handleCopy={handleCopy}
                                        copied={copied}
                                        isFavorite={isFavorite}
                                        handleToggleFavorite={handleToggleFavorite}
                                        collapsed={isCollapsed(message._id)}
                                        handleToggleCollapsed={handleToggleCollapsed}
                                        renderContentWithMentions={renderContentWithMentions}
                                        setFullMessage={setFullMessage}
                                        onContentClick={handleContentClick}
                                        typeColors={typeColors}
                                        selectionMode={selectionMode}
                                        isSelected={selectedMessages.has(message._id)}
                                        onSelect={onMessageSelect}
                                        draggable={!!onReorder && !selectionMode}
                                        isDragOver={dragOverId === message._id}
                                        isDragging={draggedId === message._id}
                                        onDragStart={(e) => handleDragStart(e, message._id)}
                                        onDragOver={(e) => handleDragOver(e, message._id)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={(e) => handleDrop(e, message._id)}
                                        onDragEnd={handleDragEnd}
                                        showColoredBorder={user?.showColoredBorder ?? true}
                                    />
                                ))}
                            </div>
                        )
                    )}

                    {/* Onglets personnalisés */}
                    {(canUseCustomTabs || planAllowsCustomTabs) && customTabs.filter(tab => tab.isVisible).map((tab) => {
                        const isActiveCustomTab = activeTab === `custom-${tab._id}`;
                        if (!isActiveCustomTab) return null;

                        const tabMessages = messages.filter(msg => tab.messageIds?.includes(msg._id));

                        return (
                            <div key={tab._id}>
                                {tabMessages.length === 0 ? (
                                    <div className="text-center py-12 text-gray-500 dark:text-gray-400 max-w-lg mx-auto px-4">
                                        <FiTag className="h-16 w-16 mx-auto mb-4 opacity-50" aria-hidden="true" />
                                        <p className="text-lg">
                                            Aucun message dans "
                                            <span 
                                                className="truncate inline-block max-w-xs align-bottom" 
                                                title={tab.name}
                                            >
                                                {tab.name}
                                            </span>
                                            "
                                        </p>
                                        <p className="text-sm mt-2">Utilisez l'action "Trier" pour organiser vos messages</p>
                                    </div>
                                ) : (
                                    <div className={gridContainerClass}>
                                        {tabMessages.map((message) => (
                                            <MessageCard
                                                key={message._id}
                                                message={message}
                                                handleCopy={handleCopy}
                                                copied={copied}
                                                isFavorite={isFavorite}
                                                handleToggleFavorite={handleToggleFavorite}
                                                collapsed={isCollapsed(message._id)}
                                                handleToggleCollapsed={handleToggleCollapsed}
                                                renderContentWithMentions={renderContentWithMentions}
                                                setFullMessage={setFullMessage}
                                                onContentClick={handleContentClick}
                                                typeColors={typeColors}
                                                selectionMode={selectionMode}
                                                isSelected={selectedMessages.has(message._id)}
                                                onSelect={onMessageSelect}
                                                draggable={!!onReorder && !selectionMode}
                                                isDragOver={dragOverId === message._id}
                                                isDragging={draggedId === message._id}
                                                onDragStart={(e) => handleDragStart(e, message._id)}
                                                onDragOver={(e) => handleDragOver(e, message._id)}
                                                onDragLeave={handleDragLeave}
                                                onDrop={(e) => handleDrop(e, message._id)}
                                                onDragEnd={handleDragEnd}
                                                showColoredBorder={user?.showColoredBorder ?? true}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className={gridContainerClass}>
                    {visibleFavorites.map((message) => (
                        <MessageCard
                            key={message._id}
                            message={message}
                            handleCopy={handleCopy}
                            copied={copied}
                            isFavorite={isFavorite}
                            handleToggleFavorite={handleToggleFavorite}
                            collapsed={isCollapsed(message._id)}
                            handleToggleCollapsed={handleToggleCollapsed}
                            renderContentWithMentions={renderContentWithMentions}
                            setFullMessage={setFullMessage}
                            onContentClick={handleContentClick}
                            typeColors={typeColors}
                            selectionMode={selectionMode}
                            isSelected={selectedMessages.has(message._id)}
                            onSelect={onMessageSelect}
                            draggable={!!onReorder && !selectionMode}
                            isDragOver={dragOverId === message._id}
                            isDragging={draggedId === message._id}
                            onDragStart={(e) => handleDragStart(e, message._id)}
                            onDragOver={(e) => handleDragOver(e, message._id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, message._id)}
                            onDragEnd={handleDragEnd}
                            showColoredBorder={user?.showColoredBorder ?? true}
                        />
                    ))}
                    {visibleMessages.map((message) => (
                        <MessageCard
                            key={message._id}
                            message={message}
                            handleCopy={handleCopy}
                            copied={copied}
                            isFavorite={isFavorite}
                            handleToggleFavorite={handleToggleFavorite}
                            collapsed={isCollapsed(message._id)}
                            handleToggleCollapsed={handleToggleCollapsed}
                            renderContentWithMentions={renderContentWithMentions}
                            setFullMessage={setFullMessage}
                            onContentClick={handleContentClick}
                            typeColors={typeColors}
                            selectionMode={selectionMode}
                            isSelected={selectedMessages.has(message._id)}
                            onSelect={onMessageSelect}
                            draggable={!!onReorder && !selectionMode}
                            isDragOver={dragOverId === message._id}
                            isDragging={draggedId === message._id}
                            onDragStart={(e) => handleDragStart(e, message._id)}
                            onDragOver={(e) => handleDragOver(e, message._id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, message._id)}
                            onDragEnd={handleDragEnd}
                            showColoredBorder={user?.showColoredBorder ?? true}
                        />
                    ))}
                </div>
            )}

            {(() => {
                // Les onglets custom affichent tous leurs messages sans pagination
                const isCustomTab = typeof activeTab === 'string' && activeTab.startsWith('custom-');
                if (isCustomTab) return null;

                const totalItems = activeTab === 'favorites' ? favoriteMessages.length : messages.length;
                const totalPages = Math.ceil(totalItems / PAGE_SIZE);
                if (totalPages <= 1) return null;

                const pages = [];
                // Afficher max 5 numéros autour de la page courante
                let startPage = Math.max(1, currentPage - 2);
                let endPage = Math.min(totalPages, startPage + 4);
                if (endPage - startPage < 4) startPage = Math.max(1, endPage - 4);

                for (let p = startPage; p <= endPage; p++) pages.push(p);

                return (
                    <div className="flex justify-end items-center gap-1 mt-4 select-none">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="px-2 py-1 rounded text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition"
                            aria-label="Page précédente"
                        >
                            ‹
                        </button>
                        {startPage > 1 && (
                            <>
                                <button onClick={() => setCurrentPage(1)} className="px-2.5 py-1 rounded text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition">1</button>
                                {startPage > 2 && <span className="px-1 text-gray-400 text-sm">…</span>}
                            </>
                        )}
                        {pages.map(p => (
                            <button
                                key={p}
                                onClick={() => setCurrentPage(p)}
                                className={`px-2.5 py-1 rounded text-sm font-medium transition ${
                                    p === currentPage
                                        ? 'bg-primary text-white dark:text-black'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                                }`}
                            >
                                {p}
                            </button>
                        ))}
                        {endPage < totalPages && (
                            <>
                                {endPage < totalPages - 1 && <span className="px-1 text-gray-400 text-sm">…</span>}
                                <button onClick={() => setCurrentPage(totalPages)} className="px-2.5 py-1 rounded text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition">{totalPages}</button>
                            </>
                        )}
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="px-2 py-1 rounded text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition"
                            aria-label="Page suivante"
                        >
                            ›
                        </button>
                        <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                            {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalItems)} / {totalItems}
                        </span>
                    </div>
                );
            })()}

            {/* Modale de tri des messages */}
            <MessageSortModal
                open={showSortModal}
                onClose={() => setShowSortModal(false)}
                selectedMessages={selectedMessages}
                customTabs={customTabs}
                onAddToTab={addMessagesToTab}
                onRemoveFromTab={removeMessagesFromTab}
                canUseCustomTabs={canUseCustomTabs}
            />
            
            {/* Modale de suppression d'onglet */}
            <DeleteCustomTabModal 
                open={!!deleteTabModal}
                onClose={() => setDeleteTabModal(null)}
                tab={deleteTabModal}
                onConfirm={confirmDeleteTab}
            />
        </>
    );
};

export default MessageList;